import React from 'react';
import { Box, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert } from '@mui/material';
import { AttachMoneyOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SectionHeaderWithCommand, SectionPaper } from '../../../../common/components';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetAssetCostsQuery } from '../../../../../services/AssetService/assetApi';
import type { AssetCostsView, AssetCostHistoryItemView } from '../../../../../services/AssetService/types/assetTypes';

const formatCurrency = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const headerCellSx = { fontWeight: 600, color: '#475569' };

const SectionShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box>
    <SectionHeaderWithCommand
      icon={<AttachMoneyOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
      title="Cost Summary"
    />
    <SectionPaper>{children}</SectionPaper>
  </Box>
);

const TotalCard: React.FC<{ label: string; value: number; highlight?: boolean }> = ({ label, value, highlight }) => (
  <Box
    sx={{
      flex: 1,
      p: 1.5,
      backgroundColor: highlight ? '#f0fdf4' : '#ffffff',
      borderRadius: 1,
      border: `1px solid ${highlight ? '#bbf7d0' : '#e2e8f0'}`,
    }}
  >
    <Typography variant="caption" color={highlight ? '#16a34a' : '#64748b'}>{label}</Typography>
    <Typography variant="h6" fontWeight={highlight ? 700 : 600} color={highlight ? '#15803d' : '#1e293b'}>
      {formatCurrency(value)}
    </Typography>
  </Box>
);

const CostHistoryRow: React.FC<{ item: AssetCostHistoryItemView; onOpen: (ticketId: string) => void }> = ({ item, onOpen }) => (
  <TableRow
    hover
    onClick={() => onOpen(item.ticketId)}
    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f0f9ff' } }}
  >
    <TableCell>{formatDate(item.date)}</TableCell>
    <TableCell>
      <Chip
        label={item.ticketId.substring(0, 8)}
        size="small"
        clickable
        sx={{
          fontSize: '0.75rem',
          height: 22,
          backgroundColor: '#eff6ff',
          color: '#2563eb',
          '&:hover': { backgroundColor: '#dbeafe' },
        }}
      />
    </TableCell>
    <TableCell>
      <Chip label={item.category} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 22 }} />
    </TableCell>
    <TableCell align="right">{formatCurrency(item.laborCost)}</TableCell>
    <TableCell align="right">{formatCurrency(item.partsCost)}</TableCell>
    <TableCell align="right">{formatCurrency(item.vendorCost)}</TableCell>
    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.total)}</TableCell>
  </TableRow>
);

const CostHistoryTable: React.FC<{ history: AssetCostHistoryItemView[]; onOpen: (ticketId: string) => void }> = ({ history, onOpen }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
      Cost History
    </Typography>
    <TableContainer sx={{ backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            <TableCell sx={headerCellSx}>Date</TableCell>
            <TableCell sx={headerCellSx}>Ticket</TableCell>
            <TableCell sx={headerCellSx}>Type</TableCell>
            <TableCell align="right" sx={headerCellSx}>Labor</TableCell>
            <TableCell align="right" sx={headerCellSx}>Parts</TableCell>
            <TableCell align="right" sx={headerCellSx}>Vendor</TableCell>
            <TableCell align="right" sx={headerCellSx}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((item) => (
            <CostHistoryRow key={item.ticketId} item={item} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const CostTotals: React.FC<{ costs: AssetCostsView }> = ({ costs }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
      Totals ({costs.ticketCount} completed ticket{costs.ticketCount !== 1 ? 's' : ''})
    </Typography>
    <Stack direction="row" spacing={2}>
      <TotalCard label="Labor" value={costs.totalLaborCost} />
      <TotalCard label="Parts" value={costs.totalPartsCost} />
      <TotalCard label="Vendor" value={costs.totalVendorCost} />
      <TotalCard label="Total" value={costs.totalCost} highlight />
    </Stack>
  </Box>
);

/**
 * AssetCostSummarySection - Displays aggregate cost data and cost history
 * for an asset based on resolved or closed PM and FAC tickets that have a cost record
 * (`GET Assets/{id}/costs`).
 * Only shown when an asset has an ID (i.e., during edit, not create).
 */
export const AssetCostSummarySection: React.FC = () => {
  const navigate = useNavigate();
  const assetId = useAppSelector(state => state.asset.currentAsset?.id);

  const { data: costs, isLoading, isError } = useGetAssetCostsQuery(assetId!, {
    skip: !assetId,
  });

  if (!assetId) return null;

  if (isLoading) {
    return (
      <SectionShell>
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      </SectionShell>
    );
  }

  if (isError) {
    return (
      <SectionShell>
        <Alert severity="warning" sx={{ borderRadius: 1 }}>
          Unable to load cost summary data.
        </Alert>
      </SectionShell>
    );
  }

  if (!costs || costs.ticketCount === 0) {
    return (
      <SectionShell>
        <Typography variant="body2" color="#64748b" textAlign="center" py={2}>
          No cost data available. Costs are captured when PM or FAC tickets are completed.
        </Typography>
      </SectionShell>
    );
  }

  return (
    <SectionShell>
      <Stack spacing={3}>
        <CostTotals costs={costs} />
        {costs.costHistory.length > 0 && (
          <CostHistoryTable
            history={costs.costHistory}
            onOpen={(ticketId) => navigate(`/tickets/edit?id=${ticketId}`)}
          />
        )}
      </Stack>
    </SectionShell>
  );
};

export default AssetCostSummarySection;
